import {
  Box,
  Checkbox,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  makeStyles,
  MenuItem,
  Select,
  Tooltip,
} from '@material-ui/core'
import { CropOutlined } from '@material-ui/icons'
import { ViewType } from '../../../viewType'
import { useMemo } from 'react'
import ReactHotkeys from 'react-hot-keys'

type ScheduleColumnSelectionActionsProps = {
  isInSelectionMode: boolean
  setIsInSelectionMode: (isInSelectionMode: boolean) => void
  setCurrentContentType: (contentType: ViewType[]) => void
  currentContentType: ViewType[]
}

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    width: 300,
  },
  formControl: {
    marginRight: theme.spacing(1),
  },
}))

export default function ScheduleColumnSelectionActions({
  isInSelectionMode,
  setIsInSelectionMode,
  setCurrentContentType,
  currentContentType,
}: ScheduleColumnSelectionActionsProps) {
  const classes = useStyles()
  const possibleContentTypes = useMemo(
    () => [
      ViewType.floorPlan,
      ViewType.unitPlan,
      ViewType.plumbing,
      ViewType.mechanical,
      ViewType.lighting,
      ViewType.other,
    ],
    [],
  )
  const disableSelectionModeButton = useMemo<boolean>(
    () => isInSelectionMode || currentContentType.length === 0,
    [isInSelectionMode, currentContentType],
  )
  const handleChange = ({ target: { value } }: any) => {
    setCurrentContentType(typeof value === 'string' ? value.split(',') : value)
  }

  const handleKeyDown = (shortcut: string, event: KeyboardEvent, handle: any) => {
    if (shortcut === 'alt+a' || shortcut === 'command+a') {
      if (disableSelectionModeButton) {
        return
      }
      setIsInSelectionMode(true)
    } else if (shortcut === 'esc') {
      if (!disableSelectionModeButton) {
        return
      }
      setIsInSelectionMode(false)
    }
  }

  return (
    <>
      <ReactHotkeys keyName={'alt+a, command+a'} onKeyDown={handleKeyDown}/>
      <ReactHotkeys keyName={'esc'} onKeyDown={handleKeyDown}/>
      <Box className={classes.container}>
        <FormControl className={classes.formControl} fullWidth>
          <InputLabel id='schedule-content-type-select-label'>
            Schedule content type
          </InputLabel>
          <Select
            id='schedule-content-type-select'
            label='Schedule content type'
            value={currentContentType}
            onChange={handleChange}
            multiple
            renderValue={(selected: unknown) =>
              (selected as string[]).join(', ')
            }
            // Workaround a React bug
            MenuProps={{ getContentAnchorEl: null }}
            color='secondary'
          >
            {possibleContentTypes.map((value) => (
              <MenuItem key={value} value={value}>
                <Checkbox checked={currentContentType.indexOf(value) > -1} />
                <ListItemText primary={value} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Tooltip
          title={
            disableSelectionModeButton
              ? 'Must select at least one schedule content type'
              : ''
          }
        >
          <span>
            <InputLabel style={{ fontSize: '10px' }}>(Alt+A or Cmd+A)</InputLabel>
            <IconButton
              disabled={disableSelectionModeButton}
              color='secondary'
              onClick={() => setIsInSelectionMode(true)}  
              title="Enter selection mode (Alt+A or Cmd+A)"
            >
              <CropOutlined />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </>
  )
}
